// ייבוא מפרט לאתר: PDF / קישור / טקסט → עבודת חילוץ → מקורות → יצירת גרסת מפרט לבדיקה.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SPEC_FIELD_LABELS, type SpecFieldKey } from '@bb/schema';
import { api, waitForJob, type Job } from '../../api';
import { Empty, ErrorBox, Field, Loading, Modal, fmtDate, useLoad, useMe } from '../ui';
import { JobProgress, errMsg, fmtValue, useAction } from './common';
import { isUrl } from './LinksEditor';
import type { Site } from './MediaParts';

interface Source {
  id: string; kind: 'pdf' | 'url' | 'text'; purpose: 'spec' | 'reference'; url: string | null; status: 'pending' | 'processing' | 'processed' | 'failed';
  error: string | null; pages: { page: number; chars: number; ocr: boolean }[] | null; extracted: { key: string; value: unknown; unit: string | null; sources: { page: number | null; quote: string; confidence: number; method: string }[] }[] | null;
  placement_id: string | null; created_at: string; fetched_at: string | null; text_content?: string | null; job_id?: string | null;
}
type Method = 'pdf' | 'url' | 'text';
const SRC_STATUS: Record<string, { l: string; c: string }> = { pending: { l: 'ממתין', c: 'info' }, processing: { l: 'בעיבוד', c: 'info' }, processed: { l: 'עובד', c: 'ok' }, failed: { l: 'נכשל', c: 'err' } };
const KIND_L: Record<string, string> = { pdf: 'PDF', url: 'קישור', text: 'טקסט מודבק' };

export function SpecImport({ site, onClose, onChanged }: { site: Site; onClose: () => void; onChanged: () => void }) {
  const me = useMe();
  const canEdit = me.role !== 'viewer';
  const nav = useNavigate();
  const [method, setMethod] = useState<Method>('pdf');
  const [placementId, setPlacementId] = useState('');
  const [purpose, setPurpose] = useState<'spec' | 'reference'>('spec');
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [job, setJob] = useState<Job | null>(null);
  const imp = useAction();
  const { data: sources, error: srcErr, loading, reload } = useLoad<Source[]>(`/api/spec-sources?mediaSiteId=${site.id}`);
  const [selected, setSelected] = useState<string[]>([]);
  const [buildPlacement, setBuildPlacement] = useState(site.placements[0]?.id ?? '');
  const build = useAction();
  const [detail, setDetail] = useState<string | null>(null);
  const [rechecking, setRechecking] = useState<string | null>(null);

  // רענון כל עוד יש מקורות בעיבוד
  const anyRunning = (sources ?? []).some((s) => s.status === 'pending' || s.status === 'processing');
  useEffect(() => { if (!anyRunning) return; const t = setInterval(reload, 2500); return () => clearInterval(t); }, [anyRunning]); // eslint-disable-line react-hooks/exhaustive-deps

  const urlBad = method === 'url' && url !== '' && !isUrl(url);
  const ready = method === 'pdf' ? !!file : method === 'url' ? !!url && !urlBad : text.trim().length > 20;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setJob(null);
    const r = await imp.run(async () => {
      let res: { sourceId: string; jobId: string };
      if (method === 'pdf') {
        const fd = new FormData();
        fd.set('mediaSiteId', site.id); if (placementId) fd.set('placementId', placementId); fd.set('purpose', purpose);
        fd.append('file', file!, file!.name);
        res = await api.upload('/api/spec-sources/import', fd);
      } else {
        res = await api.post('/api/spec-sources/import', { mediaSiteId: site.id, placementId: placementId || undefined, purpose, ...(method === 'url' ? { url } : { text }) });
      }
      reload();
      const j = await waitForJob(res.jobId, setJob);
      return { ...res, job: j };
    });
    if (r) {
      reload(); onChanged();
      if (r.job.status === 'succeeded') { setSelected((s) => [...new Set([...s, r.sourceId])]); setFile(null); setUrl(''); setText(''); }
    }
  };

  const recheck = async (s: Source) => {
    setRechecking(s.id);
    try {
      const { jobId } = await api.post<{ jobId: string }>(`/api/spec-sources/${s.id}/recheck`);
      await waitForJob(jobId);
    } catch (e) { imp.setError(errMsg(e)); }
    finally { setRechecking(null); reload(); }
  };

  const buildVersion = async () => {
    const r = await build.run(() => api.post<any>('/api/spec-sources/build-version', { placementId: buildPlacement, sourceIds: selected }), 'נוצרה גרסת מפרט לבדיקה');
    const id = r?.id ?? r?.current?.id;
    if (id) { onChanged(); nav(`/spec-versions/${id}`); }
  };

  const processedSel = selected.filter((id) => sources?.find((s) => s.id === id)?.status === 'processed');

  return (
    <Modal title={`ייבוא מפרט — ${site.name}`} onClose={onClose} wide>
      <div className="stack">
        {canEdit && (
          <form className="card stack" onSubmit={submit} aria-label="ייבוא מקור">
            <div className="grid cols-3">
              <Field label="מיקום (לא חובה)">
                <select value={placementId} onChange={(e) => setPlacementId(e.target.value)}>
                  <option value="">כל האתר</option>{site.placements.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
              <Field label="מטרה">
                <select value={purpose} onChange={(e) => setPurpose(e.target.value as 'spec' | 'reference')}><option value="spec">מפרט (הוראות סגירה)</option><option value="reference">רפרנס חזותי בלבד</option></select>
              </Field>
              <Field label="סוג מקור">
                <select value={method} onChange={(e) => setMethod(e.target.value as Method)}><option value="pdf">העלאת PDF</option><option value="url">קישור (HTML / PDF)</option><option value="text">הדבקת טקסט</option></select>
              </Field>
            </div>
            {method === 'pdf' && <Field label="קובץ PDF"><input type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>}
            {method === 'url' && (
              <>
                <Field label="קישור למפרט" hint="השרת מושך את המסמך (https). קישור שדורש כניסה ייכשל — אין עקיפת כניסה."><input className="ltr" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" aria-invalid={urlBad} /></Field>
                {urlBad && <div className="small" style={{ color: 'var(--danger)' }}>כתובת לא תקינה</div>}
              </>
            )}
            {method === 'text' && <Field label="טקסט המפרט"><textarea value={text} onChange={(e) => setText(e.target.value)} style={{ minHeight: 140 }} /></Field>}
            {purpose === 'reference' && <div className="alert info small">רפרנס אינו יוצר הוראות סגירה; רכיבים ממנו דורשים חילוץ ואישור לפני שימוש.</div>}
            <ErrorBox error={imp.error} />
            <JobProgress job={job} busy={imp.busy} label="חילוץ" />
            {job?.status === 'failed' && (
              <div className="row">
                <span className="small">אפשר לנסות דרך אחרת:</span>
                <button type="button" className="btn small" onClick={() => setMethod('pdf')}>העלאת קובץ</button>
                <button type="button" className="btn small" onClick={() => setMethod('text')}>הדבקת טקסט</button>
              </div>
            )}
            <div><button className="btn primary" disabled={imp.busy || !ready}>{imp.busy ? 'מעבד…' : 'ייבוא וחילוץ'}</button></div>
          </form>
        )}

        <section aria-labelledby="src-h">
          <div className="row between"><h2 id="src-h">מקורות</h2><button className="btn small" onClick={reload} disabled={loading}>רענון</button></div>
          {srcErr && <div className="alert err" role="alert">טעינת המקורות נכשלה: {srcErr}</div>}
          {loading && !sources && <Loading />}
          {sources && sources.length === 0 && <Empty>אין מקורות לאתר זה עדיין.</Empty>}
          {sources && sources.length > 0 && (
            <table className="table cards-sm">
              <thead><tr><th><span className="sr-only">בחירה</span></th><th>מקור</th><th>מטרה</th><th>סטטוס</th><th>כללים</th><th></th></tr></thead>
              <tbody>{sources.map((s) => (
                <tr key={s.id}>
                  <td><input type="checkbox" aria-label={`בחירת מקור ${s.id}`} disabled={s.status !== 'processed' || s.purpose !== 'spec'} checked={selected.includes(s.id)} onChange={(e) => setSelected((x) => (e.target.checked ? [...x, s.id] : x.filter((i) => i !== s.id)))} /></td>
                  <td data-l="מקור">
                    <strong>{KIND_L[s.kind]}</strong>{s.placement_id && <span className="small muted"> · {site.placements.find((p) => p.id === s.placement_id)?.name}</span>}
                    {s.url && <div className="url-cell">{s.url}</div>}
                    <div className="small muted">{fmtDate(s.created_at)}{s.fetched_at && ` · נמשך ${fmtDate(s.fetched_at)}`}{s.pages && ` · ${s.pages.length} עמודים${s.pages.some((p) => p.ocr) ? ' (OCR)' : ''}`}</div>
                  </td>
                  <td data-l="מטרה">{s.purpose === 'spec' ? 'מפרט' : 'רפרנס'}</td>
                  <td data-l="סטטוס">
                    <span className={`badge ${SRC_STATUS[s.status]?.c ?? ''}`}>{SRC_STATUS[s.status]?.l ?? s.status}</span>
                    {s.status === 'failed' && (
                      <div className="stack" style={{ gap: 4, marginTop: 4 }}>
                        <div className="small" style={{ color: 'var(--danger)' }}>{s.error ?? 'כשל ללא פירוט'}</div>
                        <div className="small muted">לא נוצרו כללים משוערים. ניתן להעלות קובץ או להדביק טקסט.</div>
                        {canEdit && <div className="row"><button className="btn small" onClick={() => setMethod('pdf')}>העלאת קובץ</button><button className="btn small" onClick={() => setMethod('text')}>הדבקת טקסט</button></div>}
                      </div>
                    )}
                  </td>
                  <td data-l="כללים">{s.extracted ? s.extracted.length : '—'}</td>
                  <td>
                    <div className="row">
                      <button className="btn small" onClick={() => setDetail(s.id)}>פרטים</button>
                      {canEdit && s.kind === 'url' && <button className="btn small" disabled={rechecking === s.id} onClick={() => recheck(s)}>{rechecking === s.id ? 'בודק…' : 'בדיקה מחדש'}</button>}
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </section>

        {canEdit && (
          <section className="card stack" aria-labelledby="build-h">
            <h2 id="build-h" style={{ margin: 0 }}>יצירת גרסת מפרט מהמקורות</h2>
            <p className="small muted" style={{ margin: 0 }}>הכללים מאוחדים; ערכים סותרים בין מקורות נשמרים כסתירה גלויה להכרעה ידנית — אין הכרעה שקטה. הגרסה נוצרת במצב "ממתין לאישור".</p>
            {site.placements.length === 0 ? <div className="alert warn">יש להוסיף מיקום לאתר לפני יצירת גרסה.</div> : (
              <div className="row" style={{ alignItems: 'flex-end' }}>
                <Field label="מיקום"><select value={buildPlacement} onChange={(e) => setBuildPlacement(e.target.value)}>{site.placements.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
                <span className="small">{processedSel.length} מקורות נבחרו</span>
                <button className="btn primary" disabled={build.busy || !processedSel.length || !buildPlacement} onClick={buildVersion}>{build.busy ? 'יוצר…' : 'יצירת גרסה ומעבר לבדיקה'}</button>
              </div>
            )}
            <ErrorBox error={build.error} />
          </section>
        )}
      </div>
      {detail && <SourceDetail id={detail} onClose={() => setDetail(null)} />}
    </Modal>
  );
}

function SourceDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, error, loading } = useLoad<Source>(`/api/spec-sources/${id}`);
  return (
    <Modal title="פרטי מקור" onClose={onClose} wide>
      <ErrorBox error={error} />
      {loading && <Loading />}
      {data && (
        <div className="stack">
          <dl className="kv">
            <dt>סוג</dt><dd>{KIND_L[data.kind]} · {data.purpose === 'spec' ? 'מפרט' : 'רפרנס'}</dd>
            {data.url && <><dt>כתובת</dt><dd className="url-cell">{data.url}</dd></>}
            <dt>סטטוס</dt><dd>{SRC_STATUS[data.status]?.l ?? data.status}{data.error && <span style={{ color: 'var(--danger)' }}> — {data.error}</span>}</dd>
            {data.pages && <><dt>עמודים</dt><dd>{data.pages.map((p) => `${p.page}${p.ocr ? ' (OCR)' : ''}`).join(', ')}</dd></>}
          </dl>
          <h3>כללים שחולצו</h3>
          {!data.extracted?.length ? <div className="muted small">לא חולצו כללים.</div> : (
            <table className="table cards-sm">
              <thead><tr><th>שדה</th><th>ערך</th><th>מקור</th></tr></thead>
              <tbody>{data.extracted.map((r, i) => (
                <tr key={i}>
                  <td>{SPEC_FIELD_LABELS[r.key as SpecFieldKey] ?? r.key}</td>
                  <td data-l="ערך" className="ltr">{fmtValue(r.value)}{r.unit ? ` ${r.unit}` : ''}</td>
                  <td data-l="מקור" className="small">{r.sources.map((s, j) => <div key={j}>{s.page != null && `עמ' ${s.page} · `}{Math.round(s.confidence * 100)}% · {s.method}<div className="source-quote">"{s.quote}"</div></div>)}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
          {data.text_content && (<><h3>קטע מהטקסט</h3><pre className="json-val" style={{ maxHeight: 220, overflow: 'auto', background: 'var(--surface-2)', padding: 8, direction: 'rtl', textAlign: 'right' }}>{data.text_content}</pre></>)}
        </div>
      )}
    </Modal>
  );
}

